import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  useInfiniteCampaignProgressQuery,
  useProgressMetaQuery,
} from '@/features/campaigns/api/campaignDetailApi';
import { fmtDate } from '../campaignDetailPage.utils';
import { Empty, FilterDropdown, KpiCard, Loading } from '../CampaignDetailPagePrimitives';

const PAGE_SIZE = 48;

function getTimingCardStyle(status?: string) {
  const s = String(status || '').toUpperCase();
  if (s === 'SUCCESS' || s === 'APPROVED' || s === 'PLAYED') {
    return {
      cardClass: 'border-emerald-300 bg-emerald-50/50 hover:bg-emerald-50/80 hover:shadow-sm',
      statusClass: 'text-emerald-600',
      // Show the raw API value (SUCCESS) — don't remap to a frontend-only label
      label: s,
    };
  }
  if (s === 'FAILED' || s === 'CANCEL' || s === 'CANCELLED' || s === 'EXPIRED' || s === 'REJECTED') {
    return {
      cardClass: 'border-red-300 bg-red-50/50 hover:bg-red-50/80 hover:shadow-sm',
      statusClass: 'text-red-600',
      label: s === 'FAILED' ? 'NOT PLAYED' : s,
    };
  }
  return {
    cardClass: 'border-amber-300 bg-amber-50/50 hover:bg-amber-50/80 hover:shadow-sm',
    statusClass: 'text-amber-600',
    label: s || 'PENDING',
  };
}

export function ProgressTab({ campaignId }: { campaignId?: string }) {
  const [date, setDate] = useState('');
  const [slot, setSlot] = useState('');
  const [screenId, setScreenId] = useState('');
  const isFiltered = Boolean(date || slot || screenId);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // ── Meta query: fetches filter options ONCE with no active filters ─────────
  // Options (screens / dates / slots) come from the unfiltered response.
  // If we read them from the filtered response they disappear the moment a
  // filter is applied — so they must live in a separate stable query.
  const meta = useProgressMetaQuery(campaignId);
  const metaData = meta.data?.data;

  const availableScreens = useMemo(() => metaData?.availableScreens ?? [], [metaData]);
  const uniqueDates = useMemo(() => metaData?.uniqueDates ?? [], [metaData]);
  const sortedSlots = useMemo(() => metaData?.sortedSlots ?? [], [metaData]);

  // ── Filtered infinite query ───────────────────────────────────────────────
  const q = useInfiniteCampaignProgressQuery(
    campaignId,
    {
      date: date || undefined,
      slot: slot || undefined,
      screenId: screenId || undefined,
    },
    PAGE_SIZE,
  );

  // All timing cards across every loaded page
  const playTimings = useMemo(
    () => q.data?.pages?.flatMap((p) => p.data?.playTimings ?? []) ?? [],
    [q.data?.pages],
  );

  // The first (and usually only) page of the *filtered* response holds KPI totals
  const firstPage = q.data?.pages?.[0]?.data;

  // ── KPI values — read directly from server fields, NEVER recalculate ──────
  //
  // API response fields:
  //   totalRepetitions    → Total
  //   successRepetitions  → Approved
  //   pendingRepetitions  → Pending
  //   failedRepetitions   → Failed
  //   successRate         → Rate (0-100)
  //
  // When no filter is active we use the unfiltered meta totals so the cards
  // always show the campaign-level numbers.
  const kpiSource = isFiltered ? firstPage : (metaData ?? firstPage);

  const kpi = useMemo(() => {
    const total = kpiSource?.totalRepetitions ?? kpiSource?.totalPlayTimings ?? 0;
    const success = kpiSource?.successRepetitions ?? 0;
    const pending = kpiSource?.pendingRepetitions ?? 0;
    const failed = kpiSource?.failedRepetitions ?? 0;
    const rate = kpiSource?.successRate ?? 0;
    return {
      total,
      success,
      pending,
      failed,
      rate: Number(rate).toFixed(1),
    };
  }, [kpiSource]);

  // ── Scroll / infinite-load ────────────────────────────────────────────────
  const hasNextPage = q.hasNextPage;
  const isFetchingNext = q.isFetchingNextPage;

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNext) void q.fetchNextPage();
  }, [hasNextPage, isFetchingNext, q]);

  // Reset scroll when filters change
  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0 });
  }, [date, slot, screenId]);

  // Scroll-based load-more
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const t = e.currentTarget;
    if (t.scrollHeight - t.scrollTop - t.clientHeight <= 120) loadMore();
  }, [loadMore]);

  // IntersectionObserver sentinel for load-more button
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = containerRef.current;
    if (!sentinel || !container || !hasNextPage) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry?.isIntersecting) loadMore(); },
      { root: container, rootMargin: '100px', threshold: 0.1 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [hasNextPage, loadMore]);

  // ── Filter change handlers ────────────────────────────────────────────────
  // Changing the screen resets date + slot because valid dates/slots may differ
  const handleScreenChange = (v: string) => { setScreenId(v); setDate(''); setSlot(''); };
  const handleDateChange = (v: string) => { setDate(v); setSlot(''); };

  const hasFilterOptions = availableScreens.length > 0 || uniqueDates.length > 0 || sortedSlots.length > 0;

  return (
    <div className="flex flex-col gap-5">

      {/* KPI Cards — values come directly from API, zero client-side math */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <KpiCard label="Total" value={kpi.total} color="text-[var(--dash-ink)]" bg="bg-[var(--dash-soft)]" />
        <KpiCard label="Success" value={kpi.success} color="text-emerald-700" bg="bg-emerald-50" />
        <KpiCard label="Pending" value={kpi.pending} color="text-amber-700" bg="bg-amber-50" />
        <KpiCard label="Not Played" value={kpi.failed} color="text-red-700" bg="bg-red-50" />
        <KpiCard label="Rate" value={`${kpi.rate}%`} color="text-[var(--dash-brand)]" bg="bg-blue-50" />
      </div>

      {/* Filter bar — shown only once meta has loaded */}
      {hasFilterOptions && (
        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-[var(--dash-line)] bg-white px-4 py-3">
          {availableScreens.length > 0 && (
            <FilterDropdown
              label="Screen"
              value={screenId}
              onChange={handleScreenChange}
              allLabel="All Screens"
              options={availableScreens.map((s) => ({
                value: s._id ?? '',
                label: s.screenName ?? s.screenId ?? s._id ?? '',
              }))}
            />
          )}

          {uniqueDates.length > 0 && (
            <FilterDropdown
              label="Date"
              value={date}
              onChange={handleDateChange}
              allLabel="All Dates"
              options={uniqueDates.map((d) => ({ value: d, label: fmtDate(d) }))}
            />
          )}

          {sortedSlots.length > 0 && (
            <FilterDropdown
              label="Slot"
              value={slot}
              onChange={setSlot}
              allLabel="All Slots"
              options={sortedSlots.map((s) => ({
                // Guard: hour=0 is valid (midnight); must not become '' which clears the filter
                value: s.hour != null ? String(s.hour) : '',
                label: s.formattedSlot ?? (s.hour != null ? `${s.hour}:00` : '—'),
              }))}
            />
          )}

          {isFiltered && (
            <button
              type="button"
              onClick={() => { setDate(''); setSlot(''); setScreenId(''); }}
              className="ml-auto rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)] px-3 py-2 text-xs font-semibold text-[var(--dash-muted)] transition hover:border-red-300 hover:bg-red-50 hover:text-red-600"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Timing grid */}
      {q.isPending && !playTimings.length ? (
        <Loading />
      ) : !playTimings.length ? (
        <Empty
          text={
            isFiltered
              ? 'No timings found for the selected filters.'
              : 'Progress will appear once the campaign starts playing.'
          }
        />
      ) : (
        <div className="flex flex-col gap-3 rounded-2xl border border-[var(--dash-line)] bg-white p-3 sm:p-4">
          <div className="flex items-center justify-between px-1 text-xs text-[var(--dash-muted)]">
            <span className="font-medium">
              Showing {playTimings.length} of {kpi.total} timings
            </span>
            {hasNextPage && (
              <span className="text-[11px] font-semibold text-[var(--dash-brand)]">
                Scroll down to load more
              </span>
            )}
          </div>

          <div
            ref={containerRef}
            onScroll={handleScroll}
            className="grid max-h-[540px] grid-cols-2 gap-3 overflow-y-auto p-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
          >
            {playTimings.map((t, i) => {
              const { cardClass, statusClass, label } = getTimingCardStyle(t.status);
              const displayTime =
                t.formattedTime ??
                (t.date && t.time ? `${t.date} ${t.time}` : '—');

              return (
                <div
                  key={i}
                  className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border px-2.5 py-3.5 text-center transition hover:-translate-y-0.5 ${cardClass}`}
                >
                  <span className="text-[11px] font-semibold leading-relaxed text-slate-800">
                    {displayTime}
                  </span>
                  <span className={`text-[11px] font-bold tracking-wider uppercase ${statusClass}`}>
                    {label}
                  </span>
                </div>
              );
            })}

            {/* Load-more sentinel / button */}
            {hasNextPage && (
              <div
                ref={sentinelRef}
                className="col-span-full flex flex-col items-center justify-center py-4 text-center"
              >
                <button
                  type="button"
                  disabled={isFetchingNext}
                  onClick={loadMore}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)] px-4 py-2 text-xs font-semibold text-[var(--dash-ink)] shadow-sm transition hover:border-[var(--dash-brand)] hover:bg-white hover:text-[var(--dash-brand)] disabled:opacity-50"
                >
                  <Loader2
                    className={`h-3.5 w-3.5 text-[var(--dash-brand)] ${isFetchingNext ? 'animate-spin' : ''}`}
                  />
                  <span>{isFetchingNext ? 'Loading more...' : 'Load more'}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
